import { getFirebase } from '@assets/firebase/FirebaseAdapater';
import {
    getStorageAdapater,
    StorageTarget
} from '@assets/firebase/StorageAdapater';
import { ServiceFileEntry, useStore } from '@assets/model/Store';
import { observer } from 'mobx-react';
import { RowLabel } from '../Financial/PanelCompanyYearly/PanelElements/RowLable/RowLable';
import { RowValue } from '../Financial/PanelCompanyYearly/PanelElements/RowValue/RowValue';
import { Panel } from '../Panel/Panel';
import { PanelHeader } from '../Panel/PanelHeader/PanelHeader';
import {
    PanelColumn,
    PanelRow,
    PanelTable
} from '../Panel/PanelTable/PanelTable';
import { ScrollPanel } from '../Panel/ScrollPanel/ScrollPanel';
import { StorageUpload } from '../StorageUpload/StorageUpload';
import * as style from './ProjectFiles.module.less';

import classNames from 'classnames';
import { AnimationItem, AnimationSegment } from 'lottie-web';
import { AnimatePresence, motion, usePresence } from 'motion/react';
import { useEffect, useState } from 'react';
import { LottieAnim } from '../LottieAnim/LottieAnim';
import { Flex } from '../UI/Flex/Flex';
import animData from './LoadingAnim.json';

interface IProjectFilesProps {
    requestSeeFile: (fileUrl: string, displayName: string) => void;
}

const getIsFileReady = (doc: ServiceFileEntry) => {
    if (doc.status == 'completed') {
        if (doc?.ragStatus == 'completed') {
            return true;
        }
    }
    return false;
};

const ProcessingAnimation = () => {
    const [isPresent, safeToRemove] = usePresence();
    const [didFinishAnimation, setDidFinishAnimation] = useState(false);
    const isAnimOut = !isPresent;

    const onEnterFrame = (lottie: AnimationItem) => {
        const frame = lottie.currentFrame;
        if (isPresent && frame > 84) {
            lottie.goToAndPlay(0, true);
        } else {
            if (frame > 200) {
                lottie.goToAndStop(200, true);
                setTimeout(() => {
                    setDidFinishAnimation(true);
                }, 500);
            }
        }
    };

    useEffect(() => {
        if (!isPresent && didFinishAnimation) safeToRemove();
    }, [isPresent, didFinishAnimation]);

    return (
        <LottieAnim
            width={50}
            height={50}
            data={animData}
            onFrame={onEnterFrame}
        />
    );
};

const ProjectFileButtons = (props: {
    onView: () => void;
    onDelete?: () => void;
    deleteEnabled: boolean;
}) => {
    const viewBtn = RowValue.button('View', file => {
        props.onView();
    });

    function makeDeleteButton(isEnabled: boolean) {
        return RowValue.button(
            'Delete',
            file => {
                props.onDelete();
            },
            null,
            isEnabled
        );
    }

    const deleteBtn = makeDeleteButton(props.deleteEnabled);
    return (
        <Flex
            direction="column"
            fillCell={true}
            justify="around"
            style={{ gap: '10px', marginRight: '5px' }}
        >
            {viewBtn}
            {deleteBtn}
        </Flex>
    );
};

export const ProjectFiles = observer((props: IProjectFilesProps) => {
    const [loadedOnMount, setLoadedOnMount] = useState<{
        [key: string]: boolean;
    }>(null);

    const projectFilesRaw = useStore().projectFiles.files;
    const fileKeys = Object.keys(projectFilesRaw);

    if (loadedOnMount == null) {
        var loaded = {};
        fileKeys.forEach(key => {
            const file = projectFilesRaw[key];
            loaded[key] = getIsFileReady(file);
        });
        setLoadedOnMount(loaded);
    }

    const store = useStore();
    const query = store.query;
    const reportId = store.selectedReportId;
    const companyName = store.query.companyName;

    const targetUploadStorage: StorageTarget = {
        type: 'rag',
        reportId: reportId
    };

    function seeFile(fileKey: string) {
        if (fileKey === store.selectedReportId) {
            props.requestSeeFile(query.value('pdfViewUrl'), fileKey);
        } else {
            const fileUrl = projectFilesRaw[fileKey].url;
            props.requestSeeFile(fileUrl, fileKey);
        }
    }

    function deleteFile(fileKey: string) {
        const areYouSure = window.confirm(
            'Are you sure you want to delete this file? ' + fileKey
        );

        async function deleteFile() {
            const storage = getStorageAdapater();
            const deleteFileName = projectFilesRaw[fileKey].filename;
            storage.deleteFileFromStorage(targetUploadStorage, deleteFileName);

            const fb = getFirebase();
            fb.deleteProjectFile(reportId, fileKey);
            fb.triggerScanRagFiles();
        }
        if (areYouSure) {
            deleteFile();
        }
    }

    const columns: PanelColumn[] = [
        {
            column: { source: null, display: 'Topics' },
            style: {
                justifyContent: 'start',
                alignItems: 'start'
            },
            element: RowValue.sources,
            overrideValue: (field, column) => {
                const doc = projectFilesRaw[field.source];
                return doc?.summary?.topics ?? [];
            }
        },
        {
            column: { source: 'saveKey', display: 'View' },
            element: (row: PanelRow, col: PanelColumn, data: any) => {
                const doc = projectFilesRaw[row.field.source];
                const isFileReady = getIsFileReady(doc);
                var elm = null;
                const baseMotionConfig = {
                    initial: { opacity: 0 },
                    animate: { opacity: 1 },
                    exit: { opacity: 0 }
                };

                if (isFileReady) {
                    const delay = loadedOnMount[row.field.source] ? 0 : 5.0;
                    if (delay > 0) {
                        console.log('delay', delay, row.field.source);
                    }
                    elm = (
                        <motion.div
                            {...baseMotionConfig}
                            key={doc.saveKey}
                            transition={{
                                duration: 0.3,
                                delay
                            }}
                        >
                            <ProjectFileButtons
                                onView={() => {
                                    seeFile(doc.saveKey);
                                }}
                                onDelete={() => {
                                    deleteFile(doc.saveKey);
                                    if (loadedOnMount[doc.saveKey]) {
                                        setLoadedOnMount({
                                            ...loadedOnMount,
                                            [doc.saveKey]: false
                                        });
                                    }
                                }}
                                deleteEnabled={doc.saveKey !== reportId}
                            />
                        </motion.div>
                    );
                } else {
                    const variants = {
                        initial: { opacity: 0 },
                        animate: {
                            opacity: 1
                        },
                        exit: {
                            opacity: 0,
                            transition: { duration: 0.3, delay: 4.5 } // delay out
                        }
                    };
                    elm = (
                        <motion.div
                            initial="initial"
                            animate="animate"
                            exit="exit"
                            key={doc.saveKey + '_animation'}
                            variants={variants}
                        >
                            <ProcessingAnimation />
                        </motion.div>
                    );
                }
                return (
                    <AnimatePresence mode="popLayout">{elm}</AnimatePresence>
                );
            }
        }
    ];

    fileKeys.sort((a, b) => {
        if (a === store.selectedReportId) return -1;
        if (b === store.selectedReportId) return 1;
        var bDate = new Date(projectFilesRaw[b]?.summary?.created_at ?? 0);
        var aDate = new Date(projectFilesRaw[a]?.summary?.created_at ?? 0);

        return bDate.getTime() - aDate.getTime();
    });

    const projectFiles = fileKeys.map(fileKey => {
        function makeLabelsRow(fileKey: string) {
            const file = projectFilesRaw[fileKey];
            return {
                field: {
                    source: file.saveKey,
                    display: file.filename.replace('.pdf', ''),
                    description: file?.summary?.created_at ?? 'N/A'
                },
                value: (_field, column) => {
                    try {
                        if (column.source) {
                            if (column.source === 'created_at') {
                                var ret = file.summary[column.source];
                                return ret ?? '';
                            } else {
                                return file[column.source] ?? 'N/A';
                            }
                        }
                    } catch {
                        return 'N/A';
                    }
                },
                // onClick: () => {
                //     props.requestSeeFile(file.url, file.filename);
                // },

                label: RowLabel.label,
                element: RowValue.value
            } as PanelRow;
        }

        const clsValues = classNames(style.panelTableStyle);
        return (
            <PanelTable
                key={fileKey}
                className={clsValues}
                columns={columns}
                rows={[makeLabelsRow(fileKey)]}
                noHeaderRow={true}
            ></PanelTable>
        );
    });

    return (
        // <ScrollPanel
        //     enableHideScrollUntilMouseOver={true}
        //     disableKeyboardScroll={true}
        // >
        <Panel fillCell={true} shadow={true}>
            <ScrollPanel
                disableKeyboardScroll={true}
                style={{ paddingRight: '0px' }}
            >
                <PanelHeader title="Project Files" />
                <AnimatePresence>
                    {projectFiles.map((file, index) => {
                        return (
                            <motion.div
                                key={file.key}
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -10 }}
                                transition={{
                                    duration: 0.3,
                                    delay: index * 0.1
                                }}
                            >
                                {file}
                            </motion.div>
                        );
                    })}
                </AnimatePresence>
                <StorageUpload
                    className={style.uploadButton}
                    display={`+ File`}
                    toasterMessage={`${companyName}: %name%`}
                    storageTarget={targetUploadStorage}
                    onUpload={fileName => {
                        const fb = getFirebase();
                        fb.addDummyProjectFile(reportId, fileName);
                        fb.triggerScanRagFiles();
                    }}
                />
            </ScrollPanel>
        </Panel>
    );
});
